import React from "react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { 
  User, 
  Mail, 
  Phone, 
  ShieldCheck, 
  LayoutDashboard, 
  UserEdit, 
  LogOut,
  MapPin,
  Calendar,
  Globe
} from "lucide-react";
import { Link } from "react-router-dom";

export default function ProfilePage() {
  const userStr = localStorage.getItem("user") || sessionStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-gray-50">
        <div className="text-center space-y-4">
          <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto animate-pulse">
            <User className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Not Logged In</h2>
          <p className="text-gray-500 max-w-xs">Please sign in to your account to view your profile and manage your posts.</p>
          <Link to="/login" className="inline-block px-8 py-3 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition shadow-lg shadow-green-100">
            Sign In Now
          </Link>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/";
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      
      <main className="flex-1 max-w-5xl mx-auto w-full px-6 py-12">
        <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-gray-200/50 overflow-hidden border border-gray-100">
          
          {/* Cover / Header Area */}
          <div className="h-48 bg-gradient-to-r from-green-600 via-emerald-500 to-teal-500 relative">
            <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
          </div>

          <div className="px-8 pb-12 -mt-16 relative">
            <div className="flex flex-col md:flex-row md:items-end gap-6 mb-10">
              {/* Profile Image */}
              <div className="relative">
                <img
                  src={user.picture || `https://ui-avatars.com/api/?name=${user.name}`}
                  alt="Profile"
                  className="w-40 h-40 rounded-[2rem] border-8 border-white shadow-2xl object-cover"
                />
                <div className="absolute -bottom-2 -right-2 bg-blue-500 text-white p-2 rounded-xl border-4 border-white shadow-lg">
                  <ShieldCheck className="w-6 h-6" />
                </div>
              </div>

              {/* Basic Info */}
              <div className="flex-1 pb-2">
                <div className="flex items-center gap-3 mb-1">
                  <h1 className="text-4xl font-black text-gray-900 tracking-tight">{user.name}</h1>
                  {user.googleId && (
                    <span className="px-3 py-1 bg-blue-50 text-blue-600 text-[10px] font-black uppercase tracking-widest rounded-lg border border-blue-100">
                      Google Verified
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-4 text-gray-500 font-medium">
                  <span className="flex items-center gap-1.5">
                    <Mail className="w-4 h-4" /> {user.email}
                  </span>
                  {user.phone && (
                    <span className="flex items-center gap-1.5">
                      <Phone className="w-4 h-4" /> {user.phone}
                    </span>
                  )}
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" /> Ethiopia
                  </span>
                </div>
              </div>
            </div>

            {/* Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              
              {/* Left Column: Details */}
              <div className="lg:col-span-2 space-y-8">
                {/* About Section */}
                <section>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-green-600" /> Bio
                  </h3>
                  <div className="p-6 bg-gray-50 rounded-3xl border border-gray-100 text-gray-600 leading-relaxed italic">
                    {user.bio || "No bio added yet. Tell people about yourself!"}
                  </div>
                </section>

                {/* Account Details */}
                <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-5 bg-white border border-gray-100 rounded-3xl shadow-sm hover:shadow-md transition">
                    <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center mb-3">
                      <Globe className="w-5 h-5" />
                    </div>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">Language</p>
                    <p className="font-bold text-gray-800">English, Amharic</p>
                  </div>
                  <div className="p-5 bg-white border border-gray-100 rounded-3xl shadow-sm hover:shadow-md transition">
                    <div className="w-10 h-10 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center mb-3">
                      <Calendar className="w-5 h-5" />
                    </div>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">Joined</p>
                    <p className="font-bold text-gray-800">Since 2024</p>
                  </div>
                </section>
              </div>

              {/* Right Column: Quick Actions */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h3>
                
                <Link
                  to="/my-posts"
                  className="flex items-center gap-4 p-4 w-full bg-green-50 text-green-700 rounded-2xl font-bold border-2 border-transparent hover:border-green-200 transition group"
                >
                  <div className="w-12 h-12 bg-green-600 text-white rounded-xl flex items-center justify-center group-hover:scale-110 transition">
                    <LayoutDashboard className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm">Manage Items</p>
                    <p className="text-xs opacity-70">View & Edit My Posts</p>
                  </div>
                </Link>

                <Link
                  to="/profile/edit"
                  className="flex items-center gap-4 p-4 w-full bg-blue-50 text-blue-700 rounded-2xl font-bold border-2 border-transparent hover:border-blue-200 transition group"
                >
                  <div className="w-12 h-12 bg-blue-600 text-white rounded-xl flex items-center justify-center group-hover:scale-110 transition">
                    <User className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm">Edit Profile</p>
                    <p className="text-xs opacity-70">Update Personal Info</p>
                  </div>
                </Link>

                <button
                  onClick={handleLogout}
                  className="flex items-center gap-4 p-4 w-full bg-red-50 text-red-700 rounded-2xl font-bold border-2 border-transparent hover:border-red-200 transition group mt-8"
                >
                  <div className="w-12 h-12 bg-red-600 text-white rounded-xl flex items-center justify-center group-hover:scale-110 transition">
                    <LogOut className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm">Sign Out</p>
                    <p className="text-xs opacity-70">Logout from session</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
